import * as React from 'react';

import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import type { SxProps, Theme } from '@mui/material/styles';

import type { FieldError } from 'react-hook-form';
import { gray100, gray60, gray80, red50 } from '../../../constants/colors';
import { formLabelWrapper } from '../../../constants/location';

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;

type Props = OutlinedInputProps & {
  disabled?: boolean;
  labelText?: string;
  requiredText?: string;
  error?: boolean;
  helperText?: string | FieldError | undefined;
  fieldType?: string;
  inputWidth?: string;
  width?: string;
  height?: string;
  spHeight?: string;
  spWidth?: string;
  formMb?: string | undefined;
  backgroundColor?: string;
  attentionText?: string;
  labelAttentionText?: string; // ラベル横に表示する注意書き
  labelFontSize?: string;
  /** ラベルを太字にする。複数の設定が並ぶ場所で見出しとして目立たせるときに使う */
  labelBold?: boolean;
  inputPaddingIs10px8px?: boolean;
  fontSizeIs14px?: boolean;
  description?: string;
  sx?: SxProps<Theme>;
  borderColor?: string;
};

const CustomTextfield = React.forwardRef((props: Props, ref): React.ReactElement => {
  const {
    disabled,
    labelText,
    requiredText,
    error,
    helperText,
    formMb,
    inputWidth,
    width,
    height,
    spWidth,
    spHeight,
    attentionText,
    labelAttentionText,
    labelFontSize,
    labelBold,
    fontSizeIs14px,
    backgroundColor,
    description,
    inputPaddingIs10px8px,
    sx,
    borderColor,
    ...rest
  } = props;

  function inputBorderFocusColor(): string {
    return error ? red50 : gray100;
  }

  function inputBorderColorFn(): string {
    if (error) return red50;
    if (borderColor) return borderColor;
    return gray60;
  }

  const styles = {
    formLabelWrapper,
    helper: {
      width: width ?? '100%',
      color: `${red50} !important`,
      '@media screen and (max-width:960px)': {
        marginLeft: 0,
        width: spWidth ?? '100%',
        color: `${red50} !important`,
      },
    },
    input: {
      '& .MuiInputBase-input': {
        WebkitTextFillColor: gray100,
        backgroundColor: backgroundColor ?? 'transparent',
      },
      '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: `${gray80} !important`,
        color: `${gray80} !important`,
        backgroundColor: '#F5F5F5',
      },
      '&.Mui-disabled': {
        backgroundColor: '#F5F5F5',
      },
      '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderColorFn(),
      },
      '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: inputBorderFocusColor(),
        borderWidth: '1px',
      },
      '&.MuiOutlinedInput-root:hover': {
        borderColor: inputBorderFocusColor(),
        borderWidth: '1px',
      },
      '@media screen and (max-width:960px)': {
        width: spWidth ?? '100%',
      },
      bgcolor: '#FFF',
      '@media screen and (max-width:600px)': {
        height: spHeight ?? '40px',
      },
      '& .MuiOutlinedInput-input': {
        padding: inputPaddingIs10px8px ? '10px 8px' : '16px 12px',
        fontSize: fontSizeIs14px ? '14px' : '16px',
      },
      // multiline時の親要素のpaddingを強制的に0に
      '&.MuiInputBase-multiline': {
        padding: '0 !important',
        height: 'auto',
        minHeight: height ?? '51px',
      },
      '& .MuiOutlinedInput-inputMultiline': {
        padding: inputPaddingIs10px8px ? '10px 8px !important' : '16px 12px !important',
        fontSize: fontSizeIs14px ? '14px' : '16px',
      },

      height: height ?? '51px',
      width: inputWidth ?? '400px',
    },
    label: {
      fontSize: labelFontSize ? labelFontSize : '14px',
      ...(labelBold ? { fontWeight: 700 } : {}),
    },
    required: {
      color: red50,
      fontSize: labelFontSize ? labelFontSize : '14px',
      marginLeft: '2px',
    },
    formMb: { marginBottom: formMb ?? '8px' },
    descriptionText: {
      color: gray80,
      fontSize: '12px',
      textAlign: 'left',
    },
    labelAttention: {
      color: red50,
      fontSize: '12px',
      marginLeft: '8px',
    },
  };

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  return (
    <Typography sx={styles.formMb} align="left" component="div">
      {labelText && (
        <Typography
          align="left"
          sx={{ ...styles.formLabelWrapper, display: 'flex', alignItems: 'center', gap: '4px' }}
          component="div"
        >
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>

          {requiredText && (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          )}
          {labelAttentionText && (
            <Typography component="span" sx={styles.labelAttention}>
              {labelAttentionText}
            </Typography>
          )}
        </Typography>
      )}
      {description && (
        <div style={{ marginBottom: '12px', textAlign: 'left' }}>
          {description.split(/\r?\n/).map((line, index) => (
            <Typography key={index} sx={styles.descriptionText} component="div" align="left">
              {line}
            </Typography>
          ))}
        </div>
      )}
      <OutlinedInput
        autoComplete="off"
        {...rest}
        value={rest.value ?? ''}
        sx={{ ...styles.input, ...sx }}
        ref={ref}
        disabled={disabled}
      />
      {error && (
        <FormHelperText sx={styles.helper} error={error}>
          {error && textfieldHelper()}
        </FormHelperText>
      )}
      {attentionText && (
        <FormHelperText sx={{ color: `${red50} !important` }} error={error}>
          {attentionText}
        </FormHelperText>
      )}
    </Typography>
  );
});
CustomTextfield.displayName = 'CustomTextfield';
export default CustomTextfield;
